import { useCallback, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { selectRoomSession } from '../../store/meetingSummary/roomSession'
import { useExecuteAiAgentMutation } from '../../store/ai/aiAgent'
import {
  useCreateIntegrationEntityMutation,
  useUpdateIntegrationEntityMutation,
  useLazyGetEntityQuery,
  useLazyGetEntitiesQuery,
  useGetDescribeQuery,
  useGetCrmEntitySchemaQuery,
} from '../../store/integrations/integrations'
import { useCreateEntityChangeMutation } from '../../store/meetingSupport/meetingSupport'
import { addToast } from '../../store/toast/toastSlice'
import { getIntegrationDisplayName } from '../../common/integrations'
import { AIAgentType } from '../../models/AIAgent'
import { SyncStatusView } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import { integrationObjectRoutingKey, IntegrationObjectFieldConfig, MappedIntegrationObject } from './utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EntitySchemaProperties = Record<string, any>

const mapSchemaToFields = (properties: EntitySchemaProperties | undefined): IntegrationObjectFieldConfig[] => {
  if (!properties) {
    return []
  }
  return Object.entries(properties).map(([apiName, prop]) => ({
    apiName,
    label: prop?.title || apiName,
    description: prop?.description ?? '',
    type: (prop?.fieldType as IntegrationObjectFieldConfig['type']) || 'text',
    ...(prop?.enum && prop.enum.length ? { options: prop.enum } : {}),
    ...(prop?.referenceObjectName ? { referenceObjectName: prop.referenceObjectName } : {}),
  }))
}

const CANDIDATE_FETCH_LIMIT = 500

const buildSchemaContext = (properties: EntitySchemaProperties | undefined) => ({
  type: 'object' as const,
  properties: properties ?? {},
})

const buildAgentSchemaContextWithCandidates = async (
  properties: EntitySchemaProperties | undefined,
  fetchCandidates: (referenceObjectName: string) => Promise<Array<{ id: string; fields: Record<string, string | null | undefined> }>>,
) => {
  const entries = Object.entries(properties ?? {})
  const augmentedEntries = await Promise.all(
    entries.map(async ([apiName, prop]) => {
      if (prop?.fieldType === 'reference' && prop?.referenceObjectName) {
        const candidates = await fetchCandidates(prop.referenceObjectName)
        return [apiName, { ...prop, candidates }] as const
      }
      return [apiName, prop] as const
    }),
  )
  return { type: 'object' as const, properties: Object.fromEntries(augmentedEntries) }
}

export type UpdateIntegrationObjectPhase = 'idle' | 'record' | 'generating' | 'review' | 'saving' | 'success' | 'error'

export type SuggestedValue = string | number

export interface IntegrationContactOption {
  id: string
  name: string
  externalId: string
}

const parseMetadata = (metadata: string): { external?: { meetingTemplateId?: string } } => {
  try {
    return metadata ? JSON.parse(metadata) : {}
  } catch {
    return {}
  }
}

const resolveAiValue = (
  field: IntegrationObjectFieldConfig,
  result: Record<string, SuggestedValue>,
  schemaProperties: EntitySchemaProperties,
): SuggestedValue | undefined => {
  const aiValue = result[field.apiName]
  if (field.type !== 'reference' || aiValue === undefined || aiValue === null) {
    return aiValue
  }
  const candidates = schemaProperties[field.apiName]?.candidates as Array<{ id: string }> | undefined
  return candidates?.some((c) => c.id === aiValue) ? aiValue : undefined
}

export const useUpdateIntegrationObject = () => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const dispatch = useAppDispatch()

  const [executeAiAgent] = useExecuteAiAgentMutation()
  const [createIntegrationEntity] = useCreateIntegrationEntityMutation()
  const [updateIntegrationEntity] = useUpdateIntegrationEntityMutation()
  const [createEntityChange] = useCreateEntityChangeMutation()
  const [triggerGetEntity] = useLazyGetEntityQuery()
  const [triggerGetEntities] = useLazyGetEntitiesQuery()

  const [object, setObject] = useState<MappedIntegrationObject | null>(null)
  const [phase, setPhase] = useState<UpdateIntegrationObjectPhase>('idle')
  const [selectedContact, setSelectedContact] = useState<IntegrationContactOption | null>(null)
  const [targetRecordId, setTargetRecordId] = useState<string | null | undefined>(undefined)

  const [values, setValues] = useState<Record<string, SuggestedValue>>({})
  const [originalValues, setOriginalValues] = useState<Record<string, SuggestedValue>>({})
  const [currentValues, setCurrentValues] = useState<Record<string, SuggestedValue>>({})
  const [acceptedKeys, setAcceptedKeys] = useState<Set<string>>(new Set())
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorSource, setErrorSource] = useState<'generate' | 'submit' | null>(null)
  const [syncStatus, setSyncStatus] = useState<SyncStatusView | null>(null)

  const { data: describeData } = useGetDescribeQuery(
    object ? { accountId, connector: object.serviceId, context: { objectName: object.objectName } } : skipToken,
  )
  const baseUrl: string | undefined = describeData?.data?.describe?.base_url

  const { data: entitySchemaData } = useGetCrmEntitySchemaQuery(
    object ? { accountId, connector: object.serviceId, context: { entityType: integrationObjectRoutingKey('schema', object.objectName) } } : skipToken,
  )
  const entitySchemaProperties: EntitySchemaProperties | undefined = entitySchemaData?.data?.properties
  const fields = useMemo(() => mapSchemaToFields(entitySchemaProperties), [entitySchemaProperties])

  const mode: 'create' | 'update' = typeof targetRecordId === 'string' ? 'update' : 'create'
  const contactName = selectedContact?.name ?? null
  const contactExternalId = selectedContact?.externalId ?? null
  const contactId = selectedContact?.id ?? null

  const meetingTemplateId = useMemo(() => parseMetadata(roomSession?.metadata ?? '').external?.meetingTemplateId ?? null, [roomSession])

  const applyResult = useCallback(
    (
      result: Record<string, SuggestedValue>,
      resultFields: IntegrationObjectFieldConfig[],
      existingValues: Record<string, SuggestedValue>,
      schemaProperties: EntitySchemaProperties = {},
    ) => {
      const accepted = resultFields
        .map((f) => {
          const aiValue = resolveAiValue(f, result, schemaProperties)
          return { apiName: f.apiName, value: f.type === 'reference' ? (aiValue ?? existingValues[f.apiName]) : aiValue, options: f.options }
        })
        .filter(({ value, options }) => value !== undefined && value !== null && value !== '' && (!options?.length || options.includes(String(value))))
      const next = Object.fromEntries(accepted.map(({ apiName, value }) => [apiName, value as SuggestedValue]))

      setValues(next)
      setOriginalValues(next)
      setAcceptedKeys(new Set(accepted.map(({ apiName }) => apiName)))
    },
    [],
  )

  const fetchCandidates = useCallback(
    async (referenceObjectName: string): Promise<Array<{ id: string; fields: Record<string, string | null | undefined> }>> => {
      if (!object) {
        return []
      }
      try {
        const result = await triggerGetEntities({
          accountId,
          connector: object.serviceId,
          entityType: integrationObjectRoutingKey('list', referenceObjectName),
          limit: CANDIDATE_FETCH_LIMIT,
        }).unwrap()
        return (result.items ?? []).map((item) => ({
          id: item.id,
          fields: Object.fromEntries((item.referenceValues ?? []).map((rv) => [rv.label, rv.value])),
        }))
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Search failed', messages: ['Failed to load reference options. Please try again.'] }))
        return []
      }
    },
    [accountId, object, triggerGetEntities, dispatch],
  )

  const generate = useCallback(
    async (seedValues?: Record<string, SuggestedValue>) => {
      if (!roomSession || !object) {
        return
      }
      const priorValues = seedValues ?? currentValues
      setPhase('generating')
      setErrorMessage(null)

      const agentSchema = await buildAgentSchemaContextWithCandidates(entitySchemaProperties, fetchCandidates)
      const context = {
        roomId: roomSession.roomId,
        sessionId: roomSession.id,
        meetingTemplateId,
        ...(contactId ? { contactId } : {}),
        objectName: object.objectName,
        schema: agentSchema,
        values: priorValues,
      }

      try {
        const res = await executeAiAgent({
          accountId,
          payload: { type: AIAgentType.CRM_OBJECT_UPDATE, context },
        }).unwrap()
        applyResult(res.result as Record<string, SuggestedValue>, fields, priorValues, agentSchema.properties)
        setGeneratedAt(new Date().toISOString())
        setPhase('review')
      } catch {
        setErrorMessage('We could not generate suggested field values. Please try again.')
        setErrorSource('generate')
        setPhase('error')
      }
    },
    [accountId, applyResult, contactId, currentValues, entitySchemaProperties, executeAiAgent, fetchCandidates, fields, meetingTemplateId, object, roomSession],
  )

  const reviewUpdates = useCallback(async () => {
    if (!object || targetRecordId === undefined) {
      return
    }
    if (mode === 'update' && targetRecordId) {
      setPhase('generating')
      const fetchCurrentValues = async () => {
        try {
          const detail = await triggerGetEntity({
            accountId,
            connector: object.serviceId,
            objectName: integrationObjectRoutingKey('single', object.objectName),
            entityId: targetRecordId,
          }).unwrap()
          return (detail ?? {}) as Record<string, SuggestedValue>
        } catch {
          return {}
        }
      }
      const current = await fetchCurrentValues()
      setCurrentValues(current)
      await generate(current)
    } else {
      setCurrentValues({})
      await generate({})
    }
  }, [accountId, generate, mode, object, targetRecordId, triggerGetEntity])

  const toggleAccept = useCallback((apiName: string) => {
    setAcceptedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(apiName)) {
        next.delete(apiName)
      } else {
        next.add(apiName)
      }
      return next
    })
  }, [])

  const setAllAccepted = useCallback(
    (accepted: boolean) => {
      setAcceptedKeys(accepted ? new Set(Object.keys(values)) : new Set())
    },
    [values],
  )

  const editValue = useCallback((apiName: string, value: SuggestedValue) => {
    setValues((prev) => ({ ...prev, [apiName]: value }))
    const hasVal = value !== undefined && value !== null && value !== ''
    setAcceptedKeys((prev) => {
      if (hasVal === prev.has(apiName)) {
        return prev
      }
      const next = new Set(prev)
      if (hasVal) {
        next.add(apiName)
      } else {
        next.delete(apiName)
      }
      return next
    })
  }, [])

  const resetAll = useCallback(() => {
    setValues(originalValues)
    setAcceptedKeys(new Set(Object.keys(originalValues)))
  }, [originalValues])

  const acceptedCount = acceptedKeys.size

  const submit = useCallback(async () => {
    if (!object || acceptedCount === 0) {
      return
    }
    setPhase('saving')
    setErrorMessage(null)

    const payload: Record<string, SuggestedValue> = Object.fromEntries(
      fields.filter((f) => acceptedKeys.has(f.apiName) && values[f.apiName] !== undefined).map((f) => [f.apiName, values[f.apiName]]),
    )
    const hasNameField = fields.some((f) => f.apiName === 'Name')
    if (mode === 'create' && hasNameField && (payload.Name === undefined || payload.Name === '')) {
      payload.Name = [contactName, object.displayName].filter(Boolean).join(' — ') || object.displayName
    }
    const relatedTo = contactExternalId ? { id: contactExternalId } : undefined
    const objectSchema = buildSchemaContext(entitySchemaProperties)

    const createEntityChangeRecord = async () => {
      try {
        if (!roomSession) {
          throw new Error('No room session available for entity_change creation')
        }
        const change = await createEntityChange({
          accountId,
          payload: {
            type: 'meeting',
            serviceId: object.serviceId,
            objectName: object.objectName,
            externalId: mode === 'update' ? (targetRecordId ?? undefined) : undefined,
            roomId: roomSession.roomId,
            sessionId: roomSession.id,
            ...(authenticatedUser?.id ? { userId: authenticatedUser.id } : {}),
            previousObjectValues: mode === 'update' ? currentValues : {},
            objectValues: payload,
            objectSchema,
          },
        }).unwrap()
        return change?.id
      } catch {
        return undefined
      }
    }
    const entityChangeId = await createEntityChangeRecord()

    const updateAndReturnId = async (entityId: string): Promise<string | undefined> => {
      await updateIntegrationEntity({
        accountId,
        connector: object.serviceId,
        entityType: integrationObjectRoutingKey('update', object.objectName),
        entityId,
        sourcePlatform: 'zocks',
        relatedTo,
        payload,
        entityChangeId,
      }).unwrap()
      return entityId
    }

    const createAndReturnId = async (): Promise<string | undefined> => {
      const res = await createIntegrationEntity({
        accountId,
        connector: object.serviceId,
        entityType: integrationObjectRoutingKey('create', object.objectName),
        sourcePlatform: 'zocks',
        relatedTo,
        payload,
        entityChangeId,
      }).unwrap()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entity: Record<string, any> | undefined = res?.data
      return (entity?.id ?? entity?.Id ?? entity?.externalId) as string | undefined
    }

    try {
      const externalId = mode === 'update' && targetRecordId ? await updateAndReturnId(targetRecordId) : await createAndReturnId()
      setSyncStatus({
        serviceId: object.serviceId,
        syncedAt: new Date().toISOString(),
        externalId,
        externalObjectName: object.objectName,
        baseUrl,
        type: 'aiResult',
        name: [object.displayName, contactName].filter(Boolean).join(' — '),
      })
      setPhase('success')
    } catch {
      setErrorMessage(`We could not save to ${getIntegrationDisplayName(object.serviceId)}. Please try again.`)
      setErrorSource('submit')
      setPhase('error')
    }
  }, [
    accountId,
    acceptedCount,
    acceptedKeys,
    authenticatedUser,
    baseUrl,
    contactExternalId,
    contactName,
    createIntegrationEntity,
    createEntityChange,
    currentValues,
    entitySchemaProperties,
    fields,
    mode,
    object,
    roomSession,
    targetRecordId,
    updateIntegrationEntity,
    values,
  ])

  const retry = useCallback(() => (errorSource === 'generate' ? generate() : submit()), [errorSource, generate, submit])

  const reset = useCallback(() => {
    setObject(null)
    setPhase('idle')
    setSelectedContact(null)
    setTargetRecordId(undefined)
    setValues({})
    setOriginalValues({})
    setCurrentValues({})
    setAcceptedKeys(new Set())
    setGeneratedAt(null)
    setErrorMessage(null)
    setErrorSource(null)
    setSyncStatus(null)
  }, [])

  const begin = useCallback((next: MappedIntegrationObject) => {
    setSelectedContact(null)
    setTargetRecordId(undefined)
    setValues({})
    setOriginalValues({})
    setCurrentValues({})
    setAcceptedKeys(new Set())
    setGeneratedAt(null)
    setErrorMessage(null)
    setErrorSource(null)
    setSyncStatus(null)
    setObject(next)
    setPhase('record')
  }, [])

  const selectContact = useCallback((contact: IntegrationContactOption | null) => setSelectedContact(contact), [])
  const restartSelection = useCallback(() => {
    setTargetRecordId(undefined)
    setSelectedContact(null)
    setValues({})
    setOriginalValues({})
    setCurrentValues({})
    setAcceptedKeys(new Set())
    setGeneratedAt(null)
    setErrorMessage(null)
    setErrorSource(null)
    setSyncStatus(null)
    setPhase('record')
  }, [])
  const selectRecord = useCallback((recordId: string) => setTargetRecordId(recordId), [])
  const chooseCreateNew = useCallback(async () => {
    setTargetRecordId(null)
    setCurrentValues({})
    await generate({})
  }, [generate])

  return {
    object,
    fields,
    phase,
    mode,
    contactRequired: object?.contactRequired ?? false,
    selectedContact,
    contactName,
    contactExternalId,
    selectContact,
    targetRecordId,
    selectRecord,
    chooseCreateNew,
    canReview: phase === 'record' && targetRecordId !== undefined && (!object?.contactRequired || !!selectedContact),
    reviewUpdates,
    values,
    originalValues,
    currentValues,
    acceptedKeys,
    acceptedCount,
    generatedAt,
    errorMessage,
    syncStatus,
    toggleAccept,
    setAllAccepted,
    editValue,
    resetAll,
    generate,
    submit,
    retry,
    begin,
    reset,
    restartSelection,
  }
}

export type UseUpdateIntegrationObject = ReturnType<typeof useUpdateIntegrationObject>
